'use client';

import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { getEstateCatalog } from '@/forApp/api/getEstate';
import { getMainFiltres } from '@/forApp/api/getMainFilters';
import { useApp } from '@/forApp/states/appState/appState';
import type { RealEstateCatalogResponseT } from '@/forApp/types/Entity';
import Breadcrumbs from '@/shared/Breadcrumbs';
import Button from '@/shared/Buttons/Button';
import { Icons } from '@/shared/Icons/Icons';
import CatalogFilters from '@/widgets/Catalog/CatalogFilters/CatalogFilters';
import CatalogForMap from '@/widgets/CatalogMap/CatalogForMap';
import MapPane from '@/widgets/CatalogMap/MapPane';
import type { DefaultFilterT } from '@/widgets/MainBlock/MainSearch/types';
import { useSetSearchParams } from '@/forApp/utils/hooks/useSearchParams';
import { DEFAULT_SORT } from '@/forApp/utils/constants';
import { t } from '@/forApp/utils/translations';

import styles from './styles.module.css';

type Props = {
  type: string;
  city?: string;
  adType?: string;
  isMobile?: boolean;
};

const ClientMapsCatalogPage: React.FC<Props> = ({
  type,
  city,
  adType,
  isMobile,
}) => {
  const [mainFilters, setMainFilters] = useState<DefaultFilterT | null>(null);
  const [catalog, setCatalog] = useState<RealEstateCatalogResponseT | null>(
    null,
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapBounds, setMapBounds] = useState<
    [[number, number], [number, number]] | null
  >(null);
  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([]);
  const { setIsLoading } = useApp();
  const [isOpenFilter, setIsOpenFilters] = useState<boolean>(false);
  const [isOpenCatalog, setIsOpenCatalog] = useState<boolean>(false);

  const [sp] = useSetSearchParams();
  const spString = sp?.toString() || '';
  const mergedSearchParams = useMemo(() => {
    const next: Record<string, string | string[]> = {
      sort: DEFAULT_SORT,
      ...sp.toObject(),
    };
    if (city) next['filters[city]'] = decodeURIComponent(city);
    if (adType) next['filters[ad_type]'] = decodeURIComponent(adType);
    return next;
  }, [sp, city, adType]);

  useEffect(() => {
    setIsLoading(false);
    setSelectedIds([]);
    setMapBounds(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setMainFilters(null);
    setCatalog(null);
    (async () => {
      try {
        const [mf, cat] = await Promise.all([
          getMainFiltres(type),
          getEstateCatalog(type, mergedSearchParams),
        ]);
        if (!cancelled) {
          setMainFilters(mf);
          setCatalog(cat);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error)?.message || 'Ошибка загрузки');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, spString]);

  const typeTitle = t(type);
  const crumbs = [{ link: '/', title: 'Главная' }, { title: typeTitle }];

  const filteredCatalog: RealEstateCatalogResponseT | null = useMemo(() => {
    if (!catalog) return null;
    // If there is a selection, show only selected objects regardless of bounds
    if (selectedIds && selectedIds.length > 0) {
      const selSet = new Set(selectedIds.map((v) => String(v)));
      const filtered = catalog.entities.data.filter((item) =>
        selSet.has(String(item.id)),
      );
      return {
        ...catalog,
        entities: { ...catalog.entities, data: filtered },
      } as RealEstateCatalogResponseT;
    }
    // Otherwise, use bounds filtering if available
    if (!mapBounds) return catalog;
    const [[minLat, minLon], [maxLat, maxLon]] = mapBounds;
    const filtered = catalog.entities.data.filter((item) => {
      const lat = parseFloat(item.latitude || 'NaN');
      const lon = parseFloat(item.longitude || 'NaN');
      if (!isFinite(lat) || !isFinite(lon)) return false;
      return lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon;
    });
    return {
      ...catalog,
      entities: {
        ...catalog.entities,
        data: filtered,
      },
    } as RealEstateCatalogResponseT;
  }, [catalog, mapBounds, selectedIds]);

  if (isMobile) {
    return (
      <div className={styles.mobileWrap}>
        <div className={styles.mobileMap}>
          <MapPane
            catalog={catalog}
            onBoundsChange={setMapBounds}
            onSelect={setSelectedIds}
            selectedIds={selectedIds}
            isMobile={true}
          />
        </div>
        <div className={styles.fabFilters}>
          <Button
            text={'Фильтры'}
            color={'white'}
            icon={<Icons.Filter />}
            onClick={() => {
              setIsOpenFilters(true);
              setIsOpenCatalog(false);
            }}
          />
        </div>
        <div className={styles.fabList}>
          <Button
            text={'Список'}
            color={'white'}
            icon={<Icons.Maps />}
            onClick={() => {
              setIsOpenCatalog(true);
              setIsOpenFilters(false);
            }}
          />
        </div>
        {isOpenFilter ? (
          <div
            className={`${styles.filtersOverlay} ${isOpenFilter ? styles.open : ''}`}
            aria-hidden={!isOpenFilter}
          >
            <div className={styles.filtersHeader}>
              <button
                className={styles.backBtn}
                onClick={() => setIsOpenFilters && setIsOpenFilters(false)}
                aria-label={'Назад к каталогу'}
              >
                <span className={styles.backIcon}>‹</span>
                <span>Фильтры</span>
              </button>
            </div>
            <div className={styles.filtersBody}>
              <CatalogFilters
                type={type}
                filters={mainFilters as DefaultFilterT}
                setIsOpenFilter={setIsOpenFilters}
                isMaps
                isMobile
              />
            </div>
          </div>
        ) : null}
        {isOpenCatalog ? (
          <div
            className={`${styles.filtersOverlay} ${isOpenCatalog ? styles.open : ''}`}
            aria-hidden={!isOpenCatalog}
          >
            <div className={styles.filtersHeader}>
              <button
                className={styles.backBtn}
                onClick={() => setIsOpenCatalog && setIsOpenCatalog(false)}
                aria-label={'Назад к каталогу'}
              >
                <span className={styles.backIcon}>‹</span>
                <span>Каталог</span>
              </button>
            </div>
            <div className={styles.catalogBody}>
              <div className={styles.catalogContent}>
                <CatalogForMap
                  type={type}
                  error={error}
                  mainFilters={mainFilters}
                  catalog={catalog}
                  filtersOpen={false}
                  isMobile
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  } else {
    return (
      <div className={styles.wrap}>
        <div className={styles.leftPane}>
          <div className={styles.leftHeader}>
            <div>
              <h1 className={styles.typeCatalogTitle}> {typeTitle}</h1>
              <Breadcrumbs crumbs={crumbs} />
            </div>
            <Button
              text={filtersOpen ? 'Показать объявления' : 'Фильтры'}
              color={'white'}
              icon={<Icons.Filter />}
              onClick={() => setFiltersOpen((p) => !p)}
            />
          </div>
          <CatalogForMap
            type={type}
            error={error}
            mainFilters={mainFilters}
            catalog={filteredCatalog}
            filtersOpen={filtersOpen}
            isMobile={false}
          />
        </div>
        <MapPane
          catalog={catalog}
          onBoundsChange={setMapBounds}
          onSelect={setSelectedIds}
          selectedIds={selectedIds}
        />
      </div>
    );
  }
};

export default ClientMapsCatalogPage;
